import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FloatingPortal } from '@floating-ui/react';
import { Combobox, ComboboxInput, ComboboxOptions, Label } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { WithRef } from 'support/types/withRef';

import { deserializeValue, getControlledValue, getSelectedOptions, serializeValue } from './utils';
import { ComboboxDropdownButton } from './components/ComboboxDropdownButton';
import { ComboboxDropdownOptions } from './components/ComboboxDropdownOptions';
import { ListboxHandlingOpenState } from './components/ListboxHandlingOpenState';
import { dropdownClasses } from './styles';
import { DropdownType } from './types';
import { useDropdownFloating } from './useDropdownFloating';

export const ComboboxDropdown = <TValue,>({
  label,
  description,
  required,
  emptySelectionLabel,
  options = [],
  name,
  value,
  onChange,
  onFocus,
  onBlur,
  onOpen,
  onClose,
  errors,
  fieldState,
  disabled,
  className,
  buttonClassName,
  wrapperClassName,
  id,
  showSelection = true,
  leftIcon,
  rightIcon,
  variant = 'default',
  size = 'base',
  dropdownDisplayTestId,
  multiple,
  ref,
}: WithRef<DropdownType<TValue>, HTMLInputElement>) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const resolvedFieldState = fieldState ?? (errors ? 'error' : 'default');
  const {
    wrapperRef,
    floatingRefs,
    floatingStyles,
    floatingWidth,
    getReferenceProps,
    getFloatingProps,
    createBlurHandler,
  } = useDropdownFloating();

  const handleOnBlur = createBlurHandler(onBlur);

  const isMultipleSelection = Boolean(multiple);
  const selectedOptions = getSelectedOptions(options, value, isMultipleSelection);
  const controlledValue = getControlledValue(value, isMultipleSelection);
  const isLarge = variant === 'large';
  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));

  const handleOnClose = () => {
    onClose?.();
    setSearch('');
  };

  return (
    <div ref={wrapperRef} className={wrapperClassName} id={id}>
      <input
        ref={ref}
        type="text"
        value={serializeValue(controlledValue, isMultipleSelection)}
        name={name}
        aria-label={name}
        className="hidden"
        onChange={(e) => {
          const deserializedValue = deserializeValue<TValue>(e.target.value, isMultipleSelection);
          onChange?.(deserializedValue as any);
        }}
      />

      <Combobox
        value={(controlledValue || '') as any}
        onChange={(newValue: any) => {
          onChange?.(newValue);
        }}
        disabled={disabled}
        multiple={isMultipleSelection as any}
      >
        {({ open }) => {
          return (
            <ListboxHandlingOpenState open={open} onOpen={onOpen} onClose={handleOnClose}>
              <div className={className}>
                {(label || description) && isLarge ? (
                  <div className="mb-2 flex flex-col gap-1">
                    {label ? (
                      <Label className="block text-base font-semibold text-wrap text-gray-900">
                        {t(label)}
                        {required && <span className="text-procuros-green-600"> *</span>}
                      </Label>
                    ) : null}
                    {description && <p className="text-sm text-gray-700">{description}</p>}
                  </div>
                ) : label ? (
                  <Label className="mb-1 block text-sm text-wrap text-gray-500">
                    {t(label)}
                    {required && <span className="text-procuros-green-600">*</span>}
                  </Label>
                ) : null}

                <div className="relative flex">
                  <ComboboxDropdownButton
                    selectedOptions={selectedOptions}
                    emptySelectionLabel={emptySelectionLabel}
                    showSelection={showSelection}
                    size={size}
                    buttonClassName={buttonClassName}
                    fieldState={resolvedFieldState}
                    open={open}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                    disabled={disabled}
                    name={name}
                    referenceProps={getReferenceProps({
                      ref: floatingRefs.setReference,
                    })}
                    onBlur={handleOnBlur}
                    onFocus={onFocus}
                    displayTestId={dropdownDisplayTestId}
                  />

                  <FloatingPortal>
                    <div
                      ref={floatingRefs.setFloating}
                      style={{ ...floatingStyles, width: `${floatingWidth}px` }}
                      {...getFloatingProps()}
                      className={cn(
                        !open && 'hidden',
                        dropdownClasses.list.size[size],
                        'z-50 max-h-60 w-full overflow-auto bg-white shadow-lg ring-1 ring-black/5 focus-visible:outline-hidden',
                      )}
                    >
                      <div className="sticky top-0 z-10 flex w-full items-center gap-1 border-b border-gray-200 bg-white px-2">
                        <MagnifyingGlassIcon className="size-4 shrink-0 text-gray-400" />
                        <ComboboxInput
                          ref={(input: HTMLInputElement | null) => {
                            if (input) {
                              input.focus();
                            }
                          }}
                          onChange={(e) => {
                            setSearch(e.target.value);
                          }}
                          displayValue={() => search}
                          placeholder={t('common:search.placeholder')}
                          className={cn(
                            dropdownClasses.listItem.size[size],
                            'min-w-0 border-0 px-0! text-inherit focus:border-0 focus:[box-shadow:none] focus:outline-hidden',
                          )}
                        />
                      </div>
                      <ComboboxOptions as="ul" static className="focus-visible:outline-hidden">
                        <ComboboxDropdownOptions options={filteredOptions} size={size} />
                      </ComboboxOptions>
                    </div>
                  </FloatingPortal>
                </div>

                {Boolean(errors) && <p className="mt-2 text-sm text-red-600">{errors?.message}</p>}
                {Boolean(description) && !isLarge && <p className="mt-2 text-sm text-gray-500">{description}</p>}
              </div>
            </ListboxHandlingOpenState>
          );
        }}
      </Combobox>
    </div>
  );
};
